import {
  CarFront,
  History,
  Hourglass,
  LayoutList,
  PackageCheck,
  Search,
  type LucideIcon,
} from "lucide-react";
import { useId } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ROLE_LABELS, type CaseListView, type Role } from "../../../../shared/domain.js";

/**
 * The shared workspace's six destinations (spec §11 "Navigation") and the
 * two ways they are presented: a persistent rail of links at Surface widths
 * from 1200 px, and a labelled view picker (a native `<select>`, 44 px tall)
 * when the chrome collapses into a top bar — 1024×768 and below. Both are
 * rendered; `workspace.css` shows exactly one of them for the width at hand.
 *
 * A destination's URL key is the API view name wherever one exists
 * (`?view=arrived_48h` asks the server for `arrived_48h`), so a URL says
 * exactly what the server was asked; `search` has no list view because it
 * queries `GET /api/search` across every case instead.
 */

export type SharedView =
  | "my_vehicles"
  | "all_active"
  | "arrived_48h"
  | "waiting"
  | "search"
  | "history";

export type SharedViewDefinition = Readonly<{
  key: SharedView;
  label: string;
  Icon: LucideIcon;
  /** The list view the server is asked for; `null` for the global search. */
  apiView: CaseListView | null;
  /** Plain-language note shown in the list's empty state. */
  emptyHint: string;
}>;

export const SHARED_VIEWS: readonly SharedViewDefinition[] = [
  {
    key: "my_vehicles",
    label: "My vehicles",
    Icon: CarFront,
    apiView: "my_vehicles",
    emptyHint:
      "Active orders for vehicles where you are the advisor or technician appear here.",
  },
  {
    key: "all_active",
    label: "All active",
    Icon: LayoutList,
    apiView: "all_active",
    emptyHint: "Ordered, Partially arrived and recently Arrived orders appear here.",
  },
  {
    key: "arrived_48h",
    label: "Arrived",
    Icon: PackageCheck,
    apiView: "arrived_48h",
    emptyHint: "Orders that arrived in the last 48 hours appear here.",
  },
  {
    key: "waiting",
    label: "Waiting",
    Icon: Hourglass,
    apiView: "waiting",
    emptyHint:
      "Orders still waiting on parts — Ordered, Partially arrived and Needs attention — appear here.",
  },
  {
    key: "search",
    label: "Search all",
    Icon: Search,
    apiView: null,
    emptyHint:
      "Search by RO, case number, VIN or its last eight, customer, part number, description, advisor or technician.",
  },
  {
    key: "history",
    label: "History",
    Icon: History,
    apiView: "history",
    emptyHint: "Cancelled orders and orders that arrived more than 48 hours ago appear here.",
  },
];

const VIEW_BY_KEY: ReadonlyMap<string, SharedViewDefinition> = new Map(
  SHARED_VIEWS.map((definition) => [definition.key, definition]),
);

export function sharedViewDefinition(view: SharedView): SharedViewDefinition {
  const definition = VIEW_BY_KEY.get(view);
  if (!definition) throw new Error(`Unknown shared view: ${view}`);
  return definition;
}

/** The view a URL names, or `null` when the value is missing or not one of ours. */
export function parseSharedView(value: string | null | undefined): SharedView | null {
  if (!value) return null;
  return VIEW_BY_KEY.has(value) ? (value as SharedView) : null;
}

/**
 * The list a role starts on (spec §11 "Default lists"): advisors and
 * technicians see My vehicles; managers, foremen and dispatchers see All
 * active. Parts has no assignment of its own, so it also starts on All active
 * should it ever open this workspace. Administration is a capability, not a
 * role, and never changes the default.
 */
export function defaultSharedView(role: Role): SharedView {
  switch (role) {
    case "service_advisor":
    case "technician":
      return "my_vehicles";
    case "service_manager":
    case "shop_foreman":
    case "dispatcher":
    case "parts":
    case "parts_manager":
      return "all_active";
  }
}

export const SHARED_WORKSPACE_PATH = "/workspace";

/**
 * Builds a workspace URL. `q` travels only with the search view; `case` is
 * dropped unless asked for, so changing views always returns to the list.
 */
export function sharedViewPath(
  view: SharedView,
  extra: { q?: string | null; case?: string | null } = {},
): string {
  const search = new URLSearchParams();
  search.set("view", view);
  if (view === "search" && extra.q) search.set("q", extra.q);
  if (extra.case) search.set("case", extra.case);
  return `${SHARED_WORKSPACE_PATH}?${search.toString()}`;
}

export { ROLE_LABELS };

export interface SharedNavigationProps {
  view: SharedView;
  /** The search query to keep when the person returns to Search all. */
  searchQuery?: string | null;
}

export function SharedNavigation({ view, searchQuery }: SharedNavigationProps) {
  const navigate = useNavigate();
  const pickerId = useId();

  return (
    <nav className="shared-nav" aria-label="Views">
      <ul className="shared-nav__list">
        {SHARED_VIEWS.map(({ key, label, Icon }) => {
          const current = key === view;
          return (
            <li key={key}>
              <Link
                to={sharedViewPath(key, { q: searchQuery })}
                className="shared-nav__link"
                aria-current={current ? "page" : undefined}
              >
                <Icon
                  className="shared-nav__icon"
                  aria-hidden="true"
                  focusable="false"
                />
                <span className="shared-nav__label">{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="shared-nav__picker">
        <label htmlFor={pickerId} className="shared-nav__picker-label">
          View
        </label>
        <select
          id={pickerId}
          className="text-input select-input shared-nav__select"
          data-size="touch"
          value={view}
          onChange={(event) => {
            const next = parseSharedView(event.target.value);
            if (next && next !== view) {
              navigate(sharedViewPath(next, { q: searchQuery }));
            }
          }}
        >
          {SHARED_VIEWS.map(({ key, label }) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </nav>
  );
}
